"use client"

import { Fragment, useState, useRef, useEffect } from "react"
import { Database } from "lucide-react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { useChatContext } from "../../../contexts/ChatContextProvider"
import { OPT_IN_SOURCES, SOURCE_GROUPS, SOURCE_KEYS } from "../../../contexts/chatSources"
import type { SourceKey } from "../../../contexts/chatSources"
import CantonPicker from "./CantonPicker"

// One checkbox per source (t('searchSources.<key>')), in groups; a group's
// header selects or clears all of its sources. Every source is open to every
// member of the firm (the server scopes firm documents and bank statements).
const TOTAL_SOURCES = SOURCE_KEYS.length
const DEFAULT_ON_SOURCES = SOURCE_KEYS.filter((key) => !OPT_IN_SOURCES.includes(key))

// Header of a group: checked when every source of the group is, mixed when some are.
function GroupToggle({ label, keys }: { label: string; keys: ReadonlyArray<SourceKey> }) {
  const { t } = useTranslation()
  const { contextSettings, setSources } = useChatContext()
  const ref = useRef<HTMLInputElement>(null)
  const selected = keys.filter((key) => contextSettings[key]).length
  const all = selected === keys.length
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = selected > 0 && !all
  }, [selected, all])
  return (
    <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
      <input
        ref={ref}
        type="checkbox"
        checked={all}
        onChange={() => setSources(keys, !all)}
        aria-label={t('chat.context.groupToggle', { group: label })}
        className="w-4 h-4 accent-primary-600 rounded focus:ring-primary-500"
      />
      <span>{label}</span>
    </label>
  )
}

export default function ContextSelectionButton() {
  const { t } = useTranslation()
  const { contextSettings, updateContextSetting } = useChatContext()
  const [isOpen, setIsOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<number | null>(null)
  const animationTimeoutRef = useRef<number | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ bottom: 0, left: 0 })

  // Above the button, whatever the menu's height
  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({ bottom: window.innerHeight - rect.top + 8, left: Math.max(8, rect.left - 180) })
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        closeDropdown()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current)
    }
  }, [])

  // Update position when window is resized
  useEffect(() => {
    if (isOpen) {
      const handleResize = () => updateDropdownPosition()
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }
  }, [isOpen])

  const activeCount = SOURCE_KEYS.filter((key) => contextSettings[key]).length

  // Red with no source, primary with every default-on source (the opt-in ones
  // may or may not be on), amber in between (literal
  // class names: Tailwind only keeps the classes it finds in the source)
  const allDefaultOn = DEFAULT_ON_SOURCES.every((key) => contextSettings[key])
  const iconColor = activeCount === 0 ? "text-error" : allDefaultOn ? "text-primary-600" : "text-warning"
  const badgeColor = activeCount === 0 ? "bg-error" : allDefaultOn ? "bg-primary-600" : "bg-warning"

  const openDropdown = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current)
    updateDropdownPosition()
    setIsOpen(true)
    // Small delay to allow CSS transition to work
    animationTimeoutRef.current = window.setTimeout(() => setIsVisible(true), 50)
  }

  const closeDropdown = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current)
    setIsVisible(false)
    // Wait for the animation to finish before removing from DOM
    animationTimeoutRef.current = window.setTimeout(() => setIsOpen(false), 250)
  }

  const handleMouseLeave = () => {
    // The native <select> popup of the canton picker lives outside the page: the
    // pointer moving onto it must not close the panel mid-choice
    const active = document.activeElement
    if (active instanceof HTMLSelectElement && dropdownRef.current?.contains(active)) return
    // Add a delay before starting to close the dropdown
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = window.setTimeout(() => closeDropdown(), 200)
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-label={t('chat.context.title')}
        aria-expanded={isOpen}
        onClick={() => isOpen ? closeDropdown() : openDropdown()}
        onMouseEnter={openDropdown}
        onMouseLeave={handleMouseLeave}
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors relative"
      >
        <Database className={`w-5 h-5 ${iconColor}`} />
        {/* Badge showing number of searched sources */}
        <span className={`absolute -top-1 -right-1 flex items-center justify-center min-w-4 h-4 px-0.5 text-[10px] font-bold text-white rounded-full ${badgeColor}`}>
          {activeCount}
        </span>
      </button>

      {isOpen && typeof document !== 'undefined' && createPortal(
        <div
          ref={dropdownRef}
          className={`fixed z-[9999] transition-all duration-250 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
          style={{ bottom: `${dropdownPosition.bottom}px`, left: `${dropdownPosition.left}px` }}
          onMouseEnter={openDropdown}
          onMouseLeave={handleMouseLeave}
        >
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 min-w-[320px] max-h-[70vh] overflow-y-auto">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center justify-between">
              <span className="flex items-center">
                <Database className="w-4 h-4 mr-2" />
                {t('chat.context.title')}
              </span>
              <span className="text-xs text-gray-500">{t('chat.context.count', { n: activeCount, total: TOTAL_SOURCES })}</span>
            </div>
            <div className="space-y-4">
              {SOURCE_GROUPS.map(({ group, keys }) => (
                <div key={group} className="space-y-1">
                  <GroupToggle label={t(`chat.context.groups.${group}`)} keys={keys} />
                  <div className="ml-6 space-y-1">
                    {keys.map((key) => (
                      <Fragment key={key}>
                      <label
                        className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 p-1 rounded-md transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={contextSettings[key]}
                          onChange={() => updateContextSetting(key)}
                          className="w-4 h-4 accent-primary-600 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t(`searchSources.${key}`)}</span>
                      </label>
                      {key === 'cantonal_law' && contextSettings.cantonal_law && <CantonPicker />}
                      </Fragment>
                    ))}
                    {group === 'caseLaw' && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 px-1">{t('chat.context.optInHint')}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs text-gray-500 dark:text-gray-500 border-t pt-2 border-gray-200 dark:border-gray-700">
              {t('chat.context.hint')}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
